import { ActionTree } from "vuex";
import { AuthState } from ".";
import { RootState } from "..";
import authApi from "../../api/auth.api";
import axios from "../../api/axios";
import i18n from "../../i18n";
import { EmailCredentials, LoginUserResult, RegistrationPayload, SocialCredentials, SocialProvider } from "../../models/auth.model";
import { ModifyUser } from "../../models/user.model";
import { getAccessToken } from "../../oauth";
import router from "../../router";
import { RootActionTypes } from "../root-actions";
import { ToastMessage } from "./../../models/toast-message.model";
import { RootMutationTypes } from "./../root-mutations";
import { AuthMutationTypes } from "./auth.mutations";
import * as Sentry from "@/utils/logger.util";

const LOCAL_STORAGE_TOKEN_KEY = "token";

export const AuthActionTypes = {
  GetUser: "GET_USER",
  GetSocialAccessToken: "GET_SOCIAL_ACCESS_TOKEN",
  LoginWithAccessToken: "LOGIN_WITH_ACCESS_TOKEN",
  LoginWithEmail: "LOGIN_WITH_EMAIL",
  LoginSuccess: "LOGIN_SUCCESS",
  Logout: "LOGOUT",
  Registration: "REGISTRATION",
  RegistrationSuccess: "REGISTRATION_SUCCESS",
  ModifyUser: "MODIFY_USER",
  LoginWithUserId: "LOGIN_WITH_USER_ID"
};

export const actions: ActionTree<AuthState, RootState> = {
  async [AuthActionTypes.GetSocialAccessToken]({ dispatch }, { provider }: SocialProvider) {
    try {
      const credentials = await getAccessToken(provider);
      dispatch(AuthActionTypes.LoginWithAccessToken, credentials);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [AuthActionTypes.LoginWithAccessToken]({ dispatch }, payload: SocialCredentials) {
    try {
      const result = await authApi.loginWithSocialProvider(payload);
      dispatch(AuthActionTypes.LoginSuccess, result.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [AuthActionTypes.LoginWithEmail]({ dispatch }, payload: EmailCredentials) {
    try {
      const result = await authApi.loginWithEmailPassword(payload);

      dispatch(AuthActionTypes.LoginSuccess, result.data.data);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [AuthActionTypes.LoginSuccess]({ commit }, payload: LoginUserResult) {
    const { token, user } = payload;

    axios.setToken(token);
    localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);

    commit(AuthMutationTypes.SetUser, user);
    commit(AuthMutationTypes.SetToken, token);
  },
  async [AuthActionTypes.Registration]({ dispatch }, payload: RegistrationPayload) {
    try {
      await authApi.registration(payload);
      dispatch(AuthActionTypes.RegistrationSuccess);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [AuthActionTypes.RegistrationSuccess]() {
    router.push("/auth/login");
    this.commit(RootMutationTypes.SetToastMessages, {
      title: i18n.tc("alert.title.success"),
      variant: "success",
      content: i18n.t("registration.registration_successful_message")
    } as ToastMessage);
  },
  async [AuthActionTypes.GetUser]({ commit, dispatch }) {
    try {
      const result = await authApi.getUser();
      const user = result.data.data;

      commit(AuthMutationTypes.SetUser, user);
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  [AuthActionTypes.Logout]({ commit }) {
    if (router.app.$route.path !== "/") {
      router.push("/");
    }

    axios.setToken(null);
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);

    commit(AuthMutationTypes.SetUser, null);
    commit(AuthMutationTypes.SetToken, null);
  },
  async [AuthActionTypes.ModifyUser]({ commit, dispatch }, payload: ModifyUser) {
    try {
      const response = await authApi.postUser(payload);
      commit(AuthMutationTypes.SetUser, response.data.data);
      commit(
        RootMutationTypes.SetToastMessages,
        {
          variant: "success",
          title: i18n.tc("alert.title.success"),
          content: i18n.tc("profile.update_success")
        } as ToastMessage,
        { root: true }
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  },
  async [AuthActionTypes.LoginWithUserId]({ dispatch }, payload: number) {
    try {
      const result = await authApi.loginWithUserId(payload);
      dispatch(AuthActionTypes.LoginSuccess, result.data.data);
      if (router.app.$route.path !== "/") {
        router.push("/");
      }
    } catch (error) {
      Sentry.captureException(error);
      dispatch(RootActionTypes.HandleApiError, error, { root: true });
    }
  }
};
